/**
 * Signed pours as keyon stores them (GetPours: one row per medication × day × time) turned into the
 * pours the pre-pour page draws: a pour is one set of days shared by its medications, so the Locked
 * pours grid stays a rectangle and an administration can be filed under `pourId|day|medId|time`.
 *
 * keyon keeps a re-poured slot on record but leaves it out of GetPours, so a pour a later re-pour cut
 * into can come back with medications on different days. Each distinct set of days becomes its own
 * piece: the piece with the most days keeps the pour's own id, the others are `<pourid>-<medId>`.
 * Ids depend only on what keyon returns, so they are the same on every read.
 */
export function poursFromServer(data) {
  const pours = (data && Array.isArray(data.pours)) ? data.pours : [];
  const items = (data && Array.isArray(data.items)) ? data.items : [];
  const out = [];
  pours.forEach((p) => {
    const meds = new Map();
    items.filter((i) => i.pourid === p.pourid).forEach((i) => {
      // a hold or change moves a medication to a new row; keyon names the one this slot follows to now
      const id = String(i.current_medentryid || i.medentryid);
      if (!meds.has(id)) {
        meds.set(id, { med: { id, name: i.med_name, form: i.med_form || '', dose: Number(i.dose), times: [], held: [], doses: {}, items: {} }, days: new Set() });
      }
      const entry = meds.get(id);
      entry.days.add(i.pour_day);
      if (!entry.med.times.includes(i.pour_time)) entry.med.times.push(i.pour_time);
      entry.med.doses[i.pour_time] = Number(i.dose);
      // the keyon slot behind this day and time: what an administration is recorded against
      entry.med.items[i.pour_day + '|' + i.pour_time] = i.itemid;
      if (Number(i.held) === 1) entry.med.held.push(i.pour_day + '|' + i.pour_time);
    });

    const pieces = new Map();
    meds.forEach(({ med, days }) => {
      const sorted = [...days].sort();
      const key = sorted.join('|');
      if (!pieces.has(key)) pieces.set(key, { days: sorted, meds: [] });
      pieces.get(key).meds.push(med);
    });
    const ordered = [...pieces.values()].sort((a, b) => (b.days.length - a.days.length) || a.meds[0].id.localeCompare(b.meds[0].id));
    ordered.forEach((piece, n) => {
      out.push({
        id: n === 0 ? String(p.pourid) : `${p.pourid}-${piece.meds[0].id}`,
        pourId: p.pourid,
        days: piece.days,
        signer: p.signature,
        signerName: p.signer_name,
        signerId: p.signer_unique_id,
        signerRole: p.signer_role || '',
        signedAt: p.signed_at,
        palette: Array.isArray(p.palette) ? p.palette : [],
        meds: piece.meds,
        skipped: Array.isArray(p.skipped) ? p.skipped : [],
      });
    });
  });
  return out;
}

/**
 * The template for the next pour (PPU-7): the shape of the most recently signed one — the hours it
 * offered, which medication took which of them, what was left out. Nothing about it is signed; the
 * page applies it as editable defaults. Null before the first pour.
 */
export function templateFromPours(pours) {
  if (!Array.isArray(pours) || !pours.length) return null;
  const latest = [...pours].sort((a, b) => String(b.signedAt).localeCompare(String(a.signedAt)))[0];
  const pieces = pours.filter((p) => p.pourId !== undefined ? p.pourId === latest.pourId : p.id === latest.id);
  const times = {};
  pieces.forEach((p) => p.meds.forEach((m) => { times[m.id] = [...m.times]; }));
  return {
    palette: [...(latest.palette || [])],
    times,
    skips: (latest.skipped || []).map((s) => s.id),
    savedAt: latest.signedAt,
    signedBy: latest.signerName || latest.signer,
  };
}

/**
 * Doses recorded in keyon (GetPrePourAdministrations, medlogtimes rows tied to a pour slot) filed the way
 * the page files them: `pieceId|day|medId|time`, the piece being the one poursFromServer put the slot in.
 * A record whose slot is no longer on a pour in force is left out.
 */
export function administrationsFromServer(rows, pours) {
  const where = new Map();
  (pours || []).forEach((p) => p.meds.forEach((m) => Object.entries(m.items || {}).forEach(([dayTime, itemId]) => {
    const [day, time] = dayTime.split('|');
    where.set(itemId, { key: `${p.id}|${day}|${m.id}|${time}`, time });
  })));
  const out = {};
  (rows || []).forEach((r) => {
    const at = where.get(r.itemid);
    if (!at) return;
    out[at.key] = { outcome: r.outcome, witness: r.witness_name || '', notes: r.notes || '', actualTime: r.actual_time || '',
      at: r.recorded_at, block: at.time, by: r.recorded_by || '', itemId: r.itemid };
  });
  return out;
}
